// @flow
import ResourceModel from "../base/ResourceModel";
import ResourceCollection from "../base/ResourceCollection";
import FilterCollection from "../filters/FilterCollection";
import ContentLinkModel from "./ContentLinkModel";
import Href from "../href/Href";
import ChoiceAttributeModel from "../attributes/ChoiceAttributeModel";

import type { ModularUIModel } from "../types";
import type { ModularUIResponse } from "../../modularui";
import type LinkModel from "../links/LinkModel";
import type ErrorResponse from "../error/ErrorResponse";
import ConceptIndexFilterModel from "../filters/ConceptIndexFilterModel";

/**
 * Get Index of concepts, to filter model catalog
 */
export default class ContentIndexModel extends ResourceModel {
  _filterCollection: FilterCollection;
  _content: ResourceCollection<ContentLinkModel>;

  /**
   */
  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    this._filterCollection = new FilterCollection(
      this.data.filter,
      {
        filter: this.contributions.filter,
        dynamicschema: this.data.dynamicschema,
      },
      this.modelOptions,
    );

    this._content = new ResourceCollection();
    this._content.collection = this.data._embedded
      ? this.data._embedded.results.map(
          (content) =>
            new ContentLinkModel(content.content, null, this.modelOptions),
        )
      : [];
  }

  /**
   */
  get type(): string {
    return "ContentIndex";
  }

  /**
   */
  static get modelName(): string {
    return "ContentIndexModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      data.contributions.resourcetype === "ContentSearch"
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this.items.getInitialChildModelLinks();
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    this.items.setChildModels(models, errors);
  }

  /**
   * Getting the self link of this list
   */
  get selfhref(): Href {
    const href = this.selflink.href;

    this.filterCollection.forEach((filter) => {
      filter.params.forEach((param) => {
        if (param.value) {
          href.setParameter(param.name, param.value);
        } else {
          href.removeParameter(param.name);
        }
      });
    });

    return href;
  }

  /**
   */
  get label(): string {
    return this.getContribution("label");
  }

  /**
   * Retrieve filters of conceptindex model
   */
  get filterCollection(): FilterCollection {
    return this._filterCollection;
  }

  /**
   * Get index filter
   */
  get indexfilter(): ConceptIndexFilterModel | null {
    const indexFilter = this._filterCollection.getFilterByAttributeKey("index");
    if (indexFilter instanceof ConceptIndexFilterModel) {
      return indexFilter;
    }
    return null;
  }

  /**
   * Retrieve content collection
   */
  get items(): ResourceCollection<ContentLinkModel> {
    return this._content;
  }

  /**
   */
  hasIndexFilter(): boolean {
    const filterAttribute = this.indexfilter?.attribute;

    return (
      filterAttribute instanceof ChoiceAttributeModel &&
      filterAttribute.options.length > 0
    );
  }

  /**
   */
  hasFiltersSet(): boolean {
    return this.filterCollection.some((filter) => filter.isActive());
  }

  /**
   */
  hasNoFiltersSet(): boolean {
    return !this.hasFiltersSet();
  }

  /**
   */
  getFirstCharHref(): Href {
    const filterAttribute = this.indexfilter?.attribute;

    let firstChar = "#";
    if (filterAttribute instanceof ChoiceAttributeModel) {
      firstChar = filterAttribute.options.filter(
        (option) => option.code !== "0",
      )[0].code;
    }

    return new Href(this.selfhref).addParameter("index", firstChar);
  }
}
